import { Link2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  translateImportFieldLabel,
  translateImportHighlight,
} from "@/lib/import/import-server-i18n";
import { ImportAnalysisReport } from "@/lib/import/import-types";

/**
 * « Ce que contient votre fichier » : la contrepartie immediate de l'upload.
 *
 * Deux lectures complementaires, volontairement dans cet ordre :
 *
 *   1. le **taux de remplissage** des colonnes les plus renseignees, avec de
 *      **vraies valeurs d'exemple** — l'element le plus rassurant qui existe
 *      (« oui, ce sont bien mes donnees ») ;
 *   2. les **concentrations de valeurs** (« 412 oeuvres sur 500 sont attribuees
 *      a Arthur Rimbaud »), qui apprennent quelque chose qu'un simple
 *      classement par remplissage ne dirait pas.
 *
 * Le nombre absolu est toujours affiche **avant** le pourcentage : « 380 lignes
 * sur 400 » se lit par tout le monde, « 95 % » seul invite a demander « 95 % de
 * quoi ? ».
 */
const TOP_COLUMNS = 4;

export const ImportDataHighlights = ({
  report,
}: {
  report: ImportAnalysisReport;
}) => {
  const vt = useVisibleT();
  const { t } = useTranslation();
  const top = report.fillRate.filter((entry) => entry.filled > 0).slice(0, TOP_COLUMNS);

  return (
    <section
      className="flex flex-col gap-5 p-6 rounded-xl border border-gray-200"
      aria-labelledby="import-highlights-title"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="import-highlights-title" className="text-base font-semibold">
          {vt("bo.import.highlights.title", "Ce que contient votre fichier")}
        </h3>
        <span className="text-sm text-gray-500 tabular-nums">
          {report.rowCount.toLocaleString("fr-FR")}{" "}
          {report.rowCount > 1
            ? vt("bo.import.highlights.rows", "lignes")
            : vt("bo.import.highlights.row", "ligne")}
          {" · "}
          {report.mappedColumnCount}{" "}
          {vt("bo.import.highlights.columnsRecognized", "colonnes reconnues")}
        </span>
      </header>

      {top.length === 0 ? (
        <p className="text-sm text-gray-500">
          {vt(
            "bo.import.highlights.noData",
            "Aucune donnée n'a été trouvée dans les colonnes reconnues.",
          )}
        </p>
      ) : (
        <ul className="flex flex-col gap-4">
          {top.map((entry) => (
            <li key={entry.fieldKey ?? entry.header} className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium flex items-center gap-1.5">
                  {translateImportFieldLabel(t, entry.fieldKey, entry.label)}
                  {entry.isRelation && (
                    <Link2
                      className="w-3.5 h-3.5 text-blue-500"
                      aria-label={vt(
                        "bo.import.highlights.relationColumn",
                        "colonne de liaison",
                      )}
                    />
                  )}
                </span>
                <span className="text-sm text-gray-600 tabular-nums">
                  {entry.filled.toLocaleString("fr-FR")}{" "}
                  {vt("bo.import.highlights.of", "sur")}{" "}
                  {report.rowCount.toLocaleString("fr-FR")}
                  {" · "}
                  {Math.round(entry.rate * 100)} %
                </span>
              </div>

              <Progress value={Math.round(entry.rate * 100)} className="h-2" />

              <div className="flex flex-wrap items-center gap-1.5">
                {entry.samples.map((sample) => (
                  <Badge
                    key={sample}
                    variant="secondary"
                    className="font-normal max-w-[22rem] truncate"
                    title={sample}
                  >
                    {sample}
                  </Badge>
                ))}
                {entry.distinctCount != null && (
                  <span className="text-xs text-gray-500">
                    {entry.distinctCount.toLocaleString("fr-FR")}{" "}
                    {vt("bo.import.highlights.distinctValues", "valeurs différentes")}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {report.highlights.length > 0 && (
        <div className="flex flex-col gap-2 pt-1">
          <h4 className="text-sm font-medium text-gray-700">
            {vt("bo.import.highlights.concentrations", "Ce qui revient le plus souvent")}
          </h4>
          <ul className="flex flex-col gap-1">
            {report.highlights.map((highlight) => (
              <li
                key={`${highlight.fieldKey}-${highlight.value ?? highlight.kind}`}
                className="text-sm text-gray-700"
              >
                {translateImportHighlight(t, highlight)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.emptyColumns.length > 0 && (
        <p className="text-xs text-gray-500">
          {report.emptyColumns.length}{" "}
          {report.emptyColumns.length > 1
            ? vt(
                "bo.import.highlights.emptyColumns",
                "colonnes de votre fichier sont entièrement vides :",
              )
            : vt(
                "bo.import.highlights.emptyColumn",
                "colonne de votre fichier est entièrement vide :",
              )}{" "}
          {report.emptyColumns
            .slice(0, 6)
            .map((name) => `« ${name} »`)
            .join(", ")}
          {report.emptyColumns.length > 6 &&
            ` ${vt("bo.import.highlights.andMore", "et")} ${report.emptyColumns.length - 6}…`}
          {" "}
          {vt("bo.import.highlights.emptyIgnored", "Elles seront ignorées.")}
        </p>
      )}
    </section>
  );
};
